import { useState } from "react";
import { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import SignaturePad from "../signature/SignaturePad";

export default function ReviewDialog({ open, onClose, title, subtitle, defaultProgress = 100, showProgress = true, onReview, testid = "review" }) {
  const [note, setNote] = useState("");
  const [progress, setProgress] = useState(defaultProgress);
  const [sigPath, setSigPath] = useState(null);
  const [saving, setSaving] = useState(false);

  const act = async (action) => {
    if (action === "approve" && !sigPath) { toast.error("A signature is required to approve"); return; }
    setSaving(true);
    try {
      await onReview({ action, note, progress, signaturePath: sigPath });
      setNote("");
      setSigPath(null);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-background max-h-[90vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3 py-1">
          {showProgress && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Approved progress (%)</label>
              <input type="number" min="0" max="100" value={progress} onChange={(e) => setProgress(e.target.value)}
                data-testid={`${testid}-progress-input`}
                className="w-full h-9 px-3 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Note (optional)</label>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Review notes…"
              data-testid={`${testid}-note-input`}
              className="w-full px-3 py-2 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary resize-none" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Digital signature (required to approve)</label>
            <SignaturePad onChange={setSigPath} testid={`${testid}-signature`} />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <button data-testid={`${testid}-reject-button`} onClick={() => act("reject")} disabled={saving}
            className="h-9 px-4 flex items-center gap-1.5 text-sm rounded-md border border-[#E31E24]/40 text-[#E31E24] hover:bg-[#E31E24]/10 transition-colors disabled:opacity-60">
            <XCircle size={15} />Reject
          </button>
          <button data-testid={`${testid}-approve-button`} onClick={() => act("approve")} disabled={saving || !sigPath}
            className="h-9 px-4 flex items-center gap-1.5 text-sm rounded-md bg-[#10B981] hover:bg-[#0da271] text-white font-medium transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}Approve & Sign
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
